import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CookieIcon from '@mui/icons-material/Cookie';
import CloseIcon from '@mui/icons-material/Close';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    functional: false,
    advertising: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences'));
        if (savedPreferences) {
          setCookiePreferences(savedPreferences);
        }
      } catch (e) {
        console.error('Error loading cookie preferences:', e);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      advertising: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      functional: false,
      advertising: false,
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Cannot disable necessary cookies
    setCookiePreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <Paper
        elevation={10}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: 3,
          borderRadius: 0,
          background: 'linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)',
          borderTop: '2px solid #3c005a',
        }}
      >
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <CookieIcon sx={{ color: '#3c005a', fontSize: 32, mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#3c005a', fontWeight: 'bold' }}>
                Cookie Consent
              </Typography>
              <Typography variant="body2" paragraph sx={{ color: 'text.secondary' }}>
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                By clicking "Accept All", you consent to our use of cookies. You can also customize your preferences 
                or reject non-essential cookies. For more information, please read our{' '}
                <Link
                  component={RouterLink}
                  to="/cookies"
                  sx={{ color: '#3c005a', textDecoration: 'underline', fontWeight: 'medium' }}
                >
                  Cookies Policy
                </Link>
                .
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={handleAcceptAll}
                  sx={{
                    bgcolor: '#3c005a',
                    '&:hover': { bgcolor: '#5c0080' },
                    textTransform: 'none',
                  }}
                >
                  Accept All
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowSettings(true)}
                  sx={{
                    borderColor: '#3c005a',
                    color: '#3c005a',
                    '&:hover': { borderColor: '#5c0080', bgcolor: 'rgba(60, 0, 90, 0.04)' },
                    textTransform: 'none',
                  }}
                >
                  Customize
                </Button>
                <Button
                  variant="text"
                  onClick={handleRejectAll}
                  sx={{
                    color: '#3c005a',
                    '&:hover': { bgcolor: 'rgba(60, 0, 90, 0.04)' },
                    textTransform: 'none',
                  }}
                >
                  Reject All
                </Button>
              </Stack>
            </Box>
            <Button
              onClick={handleRejectAll}
              sx={{
                minWidth: 'auto',
                p: 0.5,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <CloseIcon />
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Cookie Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: '#3c005a', color: 'white' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CookieIcon />
            <Typography variant="h6" component="span">
              Cookie Preferences
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage your cookie preferences. You can enable or disable different types of cookies below. 
            Note that necessary cookies cannot be disabled as they are essential for the website to function.
          </Typography>

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cookiePreferences.necessary}
                  disabled
                  sx={{ color: '#3c005a' }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Necessary Cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Essential for the website to function properly. These cannot be disabled.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={cookiePreferences.analytics}
                  onChange={() => handlePreferenceChange('analytics')}
                  sx={{ color: '#3c005a' }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Analytics / Performance Cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Help us understand how visitors interact with our website by collecting and reporting information.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={cookiePreferences.functional}
                  onChange={() => handlePreferenceChange('functional')}
                  sx={{ color: '#3c005a' }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Functional Cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Remember your choices and provide enhanced, personalized features.
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={cookiePreferences.advertising}
                  onChange={() => handlePreferenceChange('advertising')}
                  sx={{ color: '#3c005a' }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    Targeting / Advertising Cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Used to deliver personalized advertisements based on your interests.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            For more detailed information about how we use cookies, please read our{' '}
            <Link
              component={RouterLink}
              to="/cookies"
              onClick={() => setShowSettings(false)}
              sx={{ color: '#3c005a', textDecoration: 'underline' }}
            >
              Cookies Policy
            </Link>
            .
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setShowSettings(false)}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePreferences}
            variant="contained"
            sx={{
              bgcolor: '#3c005a',
              '&:hover': { bgcolor: '#5c0080' },
            }}
          >
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
